import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UserRequestActionsTypes } from '../../enum/user-request-actions-types.enum';
import { CplUserRequest } from 'src/app/model/cpl-user-request.model';
import { CplRequestItem } from 'src/app/model/cpl-request-item.model';
import { RequestHelpersService } from 'src/app/services/request-helpers/request-helpers.service';
import { ModalService } from 'src/app/modals/modal.service';
import { ToastrService } from 'ngx-toastr';
import { MessageRequest } from 'src/app/model/message-request.model';
import { ComplyMessageService } from 'src/app/services/comply-message.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RequestVerificationStatus } from 'src/app/requests/enums/verification-status.enum';
import { ReassignStageModalComponent } from 'src/app/requests/modals/sm-reassign-stage-modal.component';
import { ReqVerificationStage } from 'src/app/model/req-verification-stage.model';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';
import { SessionService, User } from '@edl-development/edl-common';
import { VerifyRequestModalComponent } from 'src/app/requests/modals/verify-request-modal/verify-request-modal.component';
import { ModifyDueDateModalComponent } from 'src/app/requests/modals/modify-due-date-modal.component';
import { UserRequestService } from 'src/app/services/user-request.service';
import { RequestItemActionService } from 'src/app/services/request-item-action.service';
import { CplChangeUserRequestManager } from 'src/app/model/cpl-change-user-request-manager.modal';
import { UserAssignment } from 'src/app/supervision-management/compliance-items/compliance-item/enums/user-assignment.enum';
import { USER_REQUEST_ACTION_LABELS } from 'src/app/constants/user-request.constants';
import { RequestUserStatus } from 'src/app/model/enum/request-user-status.enum';

@Component({
    selector: 'user-request-action-button',
    templateUrl: './user-request-action-button.component.html',
    styleUrls: ['./user-request-action-button.component.scss']
})
export class UserRequestActionButtonComponent {
    private _requestItem: CplRequestItem;
    private _canManageItem: boolean = false;
    private _userRequest: CplUserRequest;

    // Cached template-facing visibility flags
    private _hasVisibleActions: boolean = false;
    private _isLoggedInUserTheRequestUser: boolean = false;
    private _canTargetUserArchive: boolean = false;
    private _canTargetUserDelete: boolean = false;
    private _canTargetUserActivate: boolean = false;

    @Input()
    get requestItem() {
        return this._requestItem;
    }

    set requestItem(value: CplRequestItem) {
        this._requestItem = value;
        this._canManageItem = this.requestHelpers.canManageItem(this.requestItem, this.sessionService.user)
            && this.requestHelpers.canViewItem(this.requestItem);
        this.recomputeVisibility();
    };

    @Input()
    get userRequest(): CplUserRequest {
        return this._userRequest;
    }

    set userRequest(value: CplUserRequest) {
        this._userRequest = value;
        this.recomputeVisibility();
    }

    private _type: UserRequestActionsTypes = UserRequestActionsTypes.ALL;

    @Input()
    get type(): UserRequestActionsTypes {
        return this._type;
    }

    set type(value: UserRequestActionsTypes) {
        this._type = value;
        this.recomputeVisibility();
    }

    @Output() onRefreshList = new EventEmitter<void>();

    get CanManageItem() {
        return this._canManageItem;
    }

    get HasVisibleActions() {
        return this._hasVisibleActions;
    }

    get IsLoggedInUserTheRequestUser() {
        return this._isLoggedInUserTheRequestUser;
    }

    get CanTargetUserArchive() {
        return this._canTargetUserArchive;
    }

    get CanTargetUserDelete() {
        return this._canTargetUserDelete;
    }

    get CanTargetUserActivate() {
        return this._canTargetUserActivate;
    }

    get user(): User {
        return this.sessionService.user;
    }

    RequestVerificationStatus = RequestVerificationStatus;
    UserAssignment = UserAssignment;
    UserRequestActionsTypes = UserRequestActionsTypes;
    USER_REQUEST_ACTION_LABELS = USER_REQUEST_ACTION_LABELS;

    constructor(
        public requestHelpers: RequestHelpersService,
        private modalService: ModalService,
        private toastr: ToastrService,
        private messageService: ComplyMessageService,
        private ngbModal: NgbModal,
        private sessionService: SessionService,
        private userRequestService: UserRequestService,
        private requestItemActionService: RequestItemActionService,
    ) {}

    /** Private Limited Access (FR-011): requests access to the private item; emails Owner + POC. */
    requestAccess() {
        this.requestItemActionService.requestAccess(this.requestItem.RequestID);
    }

    //#region  Visibility Check
    private recomputeVisibility(): void {
        const ur = this._userRequest;
        this._isLoggedInUserTheRequestUser = this.isLoggedInUserTheRequestUser(ur);
        this._canTargetUserArchive = this.canTargetUserArchive(ur);
        this._canTargetUserDelete = this.canTargetUserDelete(ur);
        this._canTargetUserActivate = this.canTargetUserActivate(ur);
        this._hasVisibleActions = this.hasVisibleActions(ur);
    }

    isLoggedInUserTheRequestUser(userRequest: CplUserRequest): boolean {
        if (!userRequest) return false;
        return this.user.UserID === userRequest.UserID;
    }

    canTargetUserArchive(userRequest: CplUserRequest): boolean {
        if (!userRequest) return false;
        return this.requestHelpers.isFinalUserStatus(userRequest.UserStatus) && userRequest.UserArchived !== true;
    }

    canTargetUserDelete(userRequest: CplUserRequest): boolean {
        if (!userRequest) return false;
        return userRequest.UserStatus === RequestUserStatus.Draft
            && userRequest.UserArchived !== true
            && this.user.UserID === userRequest.ManagingUserID;
    }

    canTargetUserActivate(userRequest: CplUserRequest): boolean {
        if (!userRequest) return false;
        return userRequest.UserArchived === true;
    }

    hasVisibleActions(userRequest: CplUserRequest): boolean {
        if (!userRequest) return false;

        if (this.type === UserRequestActionsTypes.MY_ITEMS) {
            return this.canTargetUserArchive(userRequest) || this.canTargetUserDelete(userRequest) || this.canTargetUserActivate(userRequest);
        }

        if (!this.requestItem) return false;

        if (!this.isLoggedInUserTheRequestUser(userRequest)) return true;

        // Check remaining actions (message and nudge are hidden for the request user/subject)
        if (this.CanManageItem && this.requestHelpers.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.CANCELLED)) return true;
        if (this.canReassign(userRequest)) return true;
        if (this.canRemoveHours(userRequest)) return true;
        if (this.CanManageItem && this.requestHelpers.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.REOPEN)) return true;
        if (this.CanManageItem && this.requestHelpers.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.DISAPPROVED)) return true;
        if (this.CanManageItem && this.requestHelpers.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.SKIPPED)) return true;
        if (this.CanManageItem && this.requestHelpers.isFinalUserStatus(userRequest.UserStatus) && userRequest.SupervisorArchived === false) return true;
        if (this.CanManageItem && userRequest.SupervisorArchived === true) return true;
        if (this.CanManageItem && this.requestHelpers.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.FORGIVEN)) return true;
        if (this.CanManageItem && this.requestItem.FollowupRequired && this.requestHelpers.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.FOLLOWUP_DUE_DATE_CHANGED)) return true;
        if (this.CanManageItem && userRequest.ManagingUserID === this.user.UserID && this.requestHelpers.isAwaitingFollowupUserStatus(userRequest.UserStatus)) return true;
        if (this.CanManageItem && userRequest.UserID !== userRequest.ManagingUserID) return true;

        return false;
    }
    //#endregion

    //#region  Permission Check
    canReassign(userRequest: CplUserRequest) {
        if (userRequest.VerificationStage == null) {
            return false;
        }
        let currentStage = userRequest.VerificationStages.find(stage => stage.StageID == userRequest.VerificationStage.StageID);
        let isApprover: Boolean = (currentStage.Approvers.find(approver => approver.UserID == this.user.UserID) != null);
        if (this.CanManageItem && currentStage.Approvers.length > 1 && (isApprover || AccessRoleHelper.isAdmin(this.user.AccessRoleID))) {
            return true;
        }
        return false;
    }

    public canRemoveHours(userRequest: CplUserRequest) {
        return this.CanManageItem 
            && (this.isAct48Enabled() || this.isFlexEnabled())
            && this.requestHelpers.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.REOPEN)
            && userRequest.UserStatus == RequestUserStatus.Completed;
    }

    public isAct48Enabled(){
        return this.requestHelpers.isAct48Enabled(this.requestItem);
    }

    public isFlexEnabled(){
        return this.requestItem != null 
            && this.requestItem.FlexDetails != null 
            && this.requestItem.FlexDetails.Applicable;
    }

    //#endregion

    //#region Action Methods
    approveUserRequest(userRequest: CplUserRequest){
        this.openVerifyModal(userRequest, RequestVerificationStatus.APPROVED)
            .then(this.refreshList, () => {});
    }

    disapproveUserCycle(userRequest: CplUserRequest){
        this.openVerifyModal(userRequest, RequestVerificationStatus.DISAPPROVED)
            .then(this.refreshList, () => {});
    }

    skipApprovalStage(userRequest: CplUserRequest){
        this.openVerifyModal(userRequest, RequestVerificationStatus.SKIPPED)
            .then(this.refreshList, () => {});
    }

    reopenUserRequest(userRequest: CplUserRequest){

        this.openVerifyModal(userRequest, RequestVerificationStatus.REOPEN)
            .then(this.refreshList, () => {});
    }

    forgiveFollowUp(userRequest: CplUserRequest) {
        this.openVerifyModal(userRequest, RequestVerificationStatus.FORGIVEN)
            .then(this.refreshList, () => {});
    }
    modifyDueDate(userRequest: CplUserRequest) {
        this.openDueDateModal(userRequest)
            .then(this.refreshList, () => {});
    }

    messageUser(userRequest: CplUserRequest) {
        this.modalService.sendMessage(0, userRequest.UserRequestID,2)
            .then(() => {
                this.toastr.success('Message successfully sent to ' + userRequest.FirstName + ' ' + userRequest.LastName + '.');
            }, () => {});
    }

    nudgeUser(userRequest: CplUserRequest) {
        let message: MessageRequest = new MessageRequest();
        message.Type = 2;
        message.Target = 0;
        message.TypeID.push(userRequest.UserRequestID);

        this.messageService
            .nudge(message)
            .subscribe(() => {
                this.toastr.success(userRequest.FirstName + ' ' + userRequest.LastName + ' successfully nudged.');
            });
    }

    cancelUserRequest(userRequest:CplUserRequest){
        this.openVerifyModal(userRequest, RequestVerificationStatus.CANCELLED)
        .then(this.refreshList, () => {});

    }

    reassignUserRequest(userRequest: CplUserRequest) {
        this.openReassignModal(userRequest)
            .then(() => {
                let verificationStage: ReqVerificationStage = userRequest.VerificationStages.find(stage => stage.StageID == userRequest.VerificationStage.StageID);
                this.toastr.success(verificationStage.StageName + ' stage for this user request successfuly reassigned.');
                this.refreshList();
            }, () => {});
    }

    archiveUserRequest(userRequest:CplUserRequest){
        this.modalService
            .confirm(USER_REQUEST_ACTION_LABELS.ARCHIVE_FOR_SUPERVISORS, 'Archiving this request will remove it from your active view. Do you wish to proceed?')
            .then(() => {
                this.userRequestService
                    .archiveUserRequestAsSupervisor(userRequest.UserRequestID)
                    .subscribe((result) => {
                        this.toastr.success('Request successfully archived.');
                        this.refreshList(); 
                    });
            });

    }

    archiveUserRequestAsTargetUser(userRequest: CplUserRequest) {
        this.modalService
            .confirm(USER_REQUEST_ACTION_LABELS.ARCHIVE_FOR_MYSELF, 'Archiving this request will remove it from your active view. Do you wish to proceed?')
            .then(() => {
                this.userRequestService
                    .archiveUserRequestAsTargetUser(userRequest)
                    .subscribe(() => {
                        this.toastr.success('Request successfully archived.');
                        this.refreshList();
                    });
            });
    }

    deleteUserRequestAsTargetUser(userRequest: CplUserRequest) {
        this.modalService
            .confirm('Delete Request', 'Deleting this request will delete the request and any associated saved questionnaires and evidence. This action is not reversible. Would you like to proceed?')
            .then(() => {
                this.userRequestService
                    .deleteUserRequest(userRequest)
                    .subscribe(() => {
                        this.toastr.success('Request successfully deleted.');
                        this.refreshList();
                    });
            });
    }

    activateUserRequestAsTargetUser(userRequest: CplUserRequest) {
        this.userRequestService
            .activateUserRequestAsTargetUser(userRequest)
            .subscribe(() => {
                this.toastr.success('Request successfully restored.');
                this.refreshList();
            });
    }

    activateUserRequest(userRequest:CplUserRequest){
        this.userRequestService
            .activateUserRequestAsSupervisor(userRequest)
            .subscribe((result) => {
                this.toastr.success('Request successfully activated.');
                this.refreshList(); 
            });
    }

    selectNewManagerForUserRequest(userRequest: CplUserRequest) {
        const items = this.requestItem.AssignedUsers
            .filter(assignment => assignment.Type !== UserAssignment.TARGETUSER)
            .map(assignment => {
                return { label: assignment.DisplayName, value: assignment.UserID }
            });

        if (items.findIndex(item => item.value === userRequest.UserID) === -1) {
            items.push({ label: userRequest.DisplayName, value: userRequest.UserID });
        }

        const userIds = items.map(i => i.value);
        const uniqueItems = items.filter((item, index) => userIds.indexOf(item.value) === index);

        this.modalService
            .dropdown('Select User Request Manager', 'User Request Manager', userRequest.ManagingUserID, uniqueItems)
            .then((UserID: string) => {
                this.changeManager(UserID, userRequest.UserRequestID);
            }, () => {});
    }
    
    changeManager(managingUserID: string, userRequestID: string) {
        this.userRequestService
            .changeUserRequestManager(new CplChangeUserRequestManager(managingUserID, userRequestID))
            .subscribe(() => {
                this.toastr.success('Managing user successfully changed.');
                this.refreshList();
            });
            
    }

    //#endregion

    //#region private Methods
    private openDueDateModal(userRequest: CplUserRequest) {
        let modalRef = this.ngbModal.open(ModifyDueDateModalComponent, {
            backdrop: 'static'
        });

        modalRef.componentInstance.userRequest = userRequest;
        modalRef.componentInstance.requestItem = this.requestItem;
        modalRef.componentInstance.individualAction = true;
        return modalRef.result;
    }

    private openReassignModal(userRequest: CplUserRequest) {
        let modalRef = this.ngbModal.open(ReassignStageModalComponent, {
            size: 'lg',
            backdrop: 'static',
        });

        modalRef.componentInstance.userRequest = userRequest;
        return modalRef.result;
    }

    private openVerifyModal(userRequest: CplUserRequest, VerifyAction: number) {
        let modalRef = this.ngbModal.open(VerifyRequestModalComponent, {
            size: 'lg',
            backdrop: 'static'
        });

        modalRef.componentInstance.requestItem = this.requestItem;
        modalRef.componentInstance.userRequest = userRequest;
        modalRef.componentInstance.action = VerifyAction;

        return modalRef.result;
    }

    private refreshList = () => {
        this.onRefreshList.emit();
    }
    //#endregion
}
